/**
 * Search-related GraphQL queries
 * Global search functionality for patients and data
 */

export const SEARCH_QUERIES = {
  /**
   * Get patient details for ED users - simplified query with all fields and pagination
   */
  GET_ED_PATIENT_DETAILS: `
    query GetEDPatientDetails($offset: Int = 0, $limit: Int = 20) @cached {
      pep_vw_patient_details(offset: $offset, limit: $limit) {
        clinic_id
        clinic_name
        dob
        email
        emr_id
        emr_name
        emr_patient_clinic_data_id
        emr_patient_master_id
        enrollment_patient_id
        first_name
        home_number
        last_name
        middle_name
        mobile_number
        patient_group_id
        work_phone
      }
      pep_vw_patient_details_aggregate {
        aggregate {
          count
        }
      }
    }
  `,

  /**
   * Search patient details by name - uses pep_vw_patient_details with name filter
   */
  SEARCH_ED_PATIENT_BY_NAME: `
    query SearchEDPatientByName($searchStr: String!, $offset: Int = 0, $limit: Int = 100) @cached {
      pep_vw_patient_details(
        where: {
          _or: [
            { first_name: { _ilike: $searchStr } },
            { last_name: { _ilike: $searchStr } },
            { middle_name: { _ilike: $searchStr } }
          ]
        }
        offset: $offset
        limit: $limit
      ) {
        clinic_id
        clinic_name
        dob
        email
        emr_id
        emr_name
        emr_patient_clinic_data_id
        emr_patient_master_id
        enrollment_patient_id
        first_name
        home_number
        last_name
        middle_name
        mobile_number
        patient_group_id
        work_phone
      }
      pep_vw_patient_details_aggregate(
        where: {
          _or: [
            { first_name: { _ilike: $searchStr } },
            { last_name: { _ilike: $searchStr } },
            { middle_name: { _ilike: $searchStr } }
          ]
        }
      ) {
        aggregate {
          count
        }
      }
    }
  `,

  /**
   * Search patient details by first AND last name (for multi-word searches)
   */
  SEARCH_ED_PATIENT_BY_FIRST_AND_LAST_NAME: `
    query SearchEDPatientByFirstAndLastName($firstName: String!, $lastName: String!, $offset: Int = 0, $limit: Int = 100) @cached {
      pep_vw_patient_details(
        where: {
          _and: [
            { first_name: { _ilike: $firstName } },
            { last_name: { _ilike: $lastName } }
          ]
        }
        offset: $offset
        limit: $limit
      ) {
        clinic_id
        clinic_name
        dob
        email
        emr_id
        emr_name
        emr_patient_clinic_data_id
        emr_patient_master_id
        enrollment_patient_id
        first_name
        home_number
        last_name
        middle_name
        mobile_number
        patient_group_id
        work_phone
      }
      pep_vw_patient_details_aggregate(
        where: {
          _and: [
            { first_name: { _ilike: $firstName } },
            { last_name: { _ilike: $lastName } }
          ]
        }
      ) {
        aggregate {
          count
        }
      }
    }
  `,

  /**
   * Search patient details by DOB - uses pep_vw_patient_details with DOB filter
   */
  SEARCH_ED_PATIENT_BY_DOB: `
    query SearchEDPatientByDOB($searchStr: date!, $offset: Int = 0, $limit: Int = 100) @cached {
      pep_vw_patient_details(
        where: { dob: { _eq: $searchStr } }
        offset: $offset
        limit: $limit
      ) {
        clinic_id
        clinic_name
        dob
        email
        emr_id
        emr_name
        emr_patient_clinic_data_id
        emr_patient_master_id
        enrollment_patient_id
        first_name
        home_number
        last_name
        middle_name
        mobile_number
        patient_group_id
        work_phone
      }
      pep_vw_patient_details_aggregate(
        where: { dob: { _eq: $searchStr } }
      ) {
        aggregate {
          count
        }
      }
    }
  `,

  /**
   * Search patient details by phone - uses pep_vw_patient_details with phone filter
   */
  SEARCH_ED_PATIENT_BY_PHONE: `
    query SearchEDPatientByPhone($searchStr: String!, $offset: Int = 0, $limit: Int = 100) @cached {
      pep_vw_patient_details(
        where: {
          _or: [
            { mobile_number: { _ilike: $searchStr } },
            { home_number: { _ilike: $searchStr } },
            { work_phone: { _ilike: $searchStr } }
          ]
        }
        offset: $offset
        limit: $limit
      ) {
        clinic_id
        clinic_name
        dob
        email
        emr_id
        emr_name
        emr_patient_clinic_data_id
        emr_patient_master_id
        enrollment_patient_id
        first_name
        home_number
        last_name
        middle_name
        mobile_number
        patient_group_id
        work_phone
      }
      pep_vw_patient_details_aggregate(
        where: {
          _or: [
            { mobile_number: { _ilike: $searchStr } },
            { home_number: { _ilike: $searchStr } },
            { work_phone: { _ilike: $searchStr } }
          ]
        }
      ) {
        aggregate {
          count
        }
      }
    }
  `,

  /**
   * Search patient details by email - uses pep_vw_patient_details with email filter
   */
  SEARCH_ED_PATIENT_BY_EMAIL: `
    query SearchEDPatientByEmail($searchStr: String!, $offset: Int = 0, $limit: Int = 100) @cached {
      pep_vw_patient_details(
        where: { email: { _ilike: $searchStr } }
        offset: $offset
        limit: $limit
      ) {
        clinic_id
        clinic_name
        dob
        email
        emr_id
        emr_name
        emr_patient_clinic_data_id
        emr_patient_master_id
        enrollment_patient_id
        first_name
        home_number
        last_name
        middle_name
        mobile_number
        patient_group_id
        work_phone
      }
      pep_vw_patient_details_aggregate(
        where: { email: { _ilike: $searchStr } }
      ) {
        aggregate {
          count
        }
      }
    }
  `,

  /**
   * Search patient details by clinic - uses pep_vw_patient_details with clinic filter
   */
  SEARCH_ED_PATIENT_BY_CLINIC: `
    query SearchEDPatientByClinic($searchStr: String!, $offset: Int = 0, $limit: Int = 100) @cached {
      pep_vw_patient_details(
        where: { clinic_name: { _ilike: $searchStr } }
        offset: $offset
        limit: $limit
      ) {
        clinic_id
        clinic_name
        dob
        email
        emr_id
        emr_name
        emr_patient_clinic_data_id
        emr_patient_master_id
        enrollment_patient_id
        first_name
        home_number
        last_name
        middle_name
        mobile_number
        patient_group_id
        work_phone
      }
      pep_vw_patient_details_aggregate(
        where: { clinic_name: { _ilike: $searchStr } }
      ) {
        aggregate {
          count
        }
      }
    }
  `,

  /**
   * Search patient details by EMR ID - uses pep_vw_patient_details with EMR filter
   */
  SEARCH_ED_PATIENT_BY_EMR: `
    query SearchEDPatientByEMR($searchStr: String!, $offset: Int = 0, $limit: Int = 100) @cached {
      pep_vw_patient_details(
        where: { emr_id: { _ilike: $searchStr } }
        offset: $offset
        limit: $limit
      ) {
        clinic_id
        clinic_name
        dob
        email
        emr_id
        emr_name
        emr_patient_clinic_data_id
        emr_patient_master_id
        enrollment_patient_id
        first_name
        home_number
        last_name
        middle_name
        mobile_number
        patient_group_id
        work_phone
      }
      pep_vw_patient_details_aggregate(
        where: { emr_id: { _ilike: $searchStr } }
      ) {
        aggregate {
          count
        }
      }
    }
  `,

  // Global Search queries
  SEARCH_PATIENTS_DEFAULT: `
    query SearchPatientsDefault($searchStr: String!) {
      pep_vw_consolidated_data(
        where: { search_string: { _ilike: $searchStr } }
        limit: 100
      ) {
        pep_patient_id
        patient_clinic_data_id
        full_name
        dob
        home_phone_number
        mobile_phone_number
        email
        clinic_id
        clinic_name
        emr_id
      }
    }
  `,

  SEARCH_PATIENTS_BY_FIELD: `
    query SearchPatientsByField($searchStr: String!) {
      pep_vw_consolidated_data(
        where: { search_string: { _ilike: $searchStr } }
        limit: 100
      ) {
        pep_patient_id
        patient_clinic_data_id
        full_name
        dob
        home_phone_number
        mobile_phone_number
        email
        clinic_id
        clinic_name
        emr_id
      }
    }
  `,

  // Dynamic field search queries for each searchable field
  SEARCH_BY_MOBILE_PHONE: `
    query SearchByMobilePhone($searchStr: String!) {
      pep_vw_consolidated_data(
        where: { search_string: { _ilike: $searchStr } }
        limit: 100
      ) {
        pep_patient_id
        patient_clinic_data_id
        full_name
        dob
        home_phone_number
        mobile_phone_number
        email
        clinic_id
        clinic_name
        emr_id
      }
    }
  `,

  SEARCH_BY_HOME_PHONE: `
    query SearchByHomePhone($searchStr: String!) {
      pep_vw_consolidated_data(
        where: { search_string: { _ilike: $searchStr } }
        limit: 100
      ) {
        pep_patient_id
        patient_clinic_data_id
        full_name
        dob
        home_phone_number
        mobile_phone_number
        email
        clinic_id
        clinic_name
        emr_id
      }
    }
  `,

  SEARCH_BY_EMAIL: `
    query SearchByEmail($searchStr: String!) {
      pep_vw_consolidated_data(
        where: { search_string: { _ilike: $searchStr } }
        limit: 100
      ) {
        pep_patient_id
        patient_clinic_data_id
        full_name
        dob
        home_phone_number
        mobile_phone_number
        email
        clinic_id
        clinic_name
        emr_id
      }
    }
  `,

  SEARCH_BY_CLINIC_NAME: `
    query SearchByClinicName($searchStr: String!) {
      pep_vw_consolidated_data(
        where: { search_string: { _ilike: $searchStr } }
        limit: 100
      ) {
        pep_patient_id
        patient_clinic_data_id
        full_name
        dob
        home_phone_number
        mobile_phone_number
        email
        clinic_id
        clinic_name
        emr_id
      }
    }
  `,

  SEARCH_BY_EMR_ID: `
    query SearchByEmrId($searchStr: String!) {
      pep_vw_consolidated_data(
        where: { search_string: { _ilike: $searchStr } }
        limit: 100
      ) {
        pep_patient_id
        patient_clinic_data_id
        full_name
        dob
        home_phone_number
        mobile_phone_number
        email
        clinic_id
        clinic_name
        emr_id
      }
    }
  `,

  GET_ALL_PATIENTS: `
    query GetAllPatients {
      pep_vw_consolidated_data(
        limit: 100
        order_by: { full_name: asc }
      ) {
        pep_patient_id
        patient_clinic_data_id
        full_name
        dob
        home_phone_number
        mobile_phone_number
        email
        clinic_id
        clinic_name
        emr_id
      }
    }
  `,

  SEARCH_PATIENTS_BY_DOB: `
    query SearchPatientsByDOB($searchStr: date!) {
      pep_vw_consolidated_data(
        where: { dob: { _eq: $searchStr } }
        limit: 100
      ) {
        pep_patient_id
        patient_clinic_data_id
        full_name
        dob
        home_phone_number
        mobile_phone_number
        email
        clinic_id
        clinic_name
        emr_id
      }
    }
  `,

  // Search by Patient Name (from GRAPHQL_QUERIES)
  SEARCH_BY_PATIENT_NAME: `
    query MyQuery($searchStr: String!) {
      pep_vw_consolidated_data(where: { search_string: { _ilike: $searchStr } } limit: 100) {
        pep_patient_id
        patient_clinic_data_id
        full_name
        dob
        home_phone_number
        mobile_phone_number
        email
        clinic_id
        clinic_name
        emr_id
      }
    }
  `,

  // Search by Date of Birth (from GRAPHQL_QUERIES)
  SEARCH_BY_DATE_OF_BIRTH: `
    query MyQuery($searchStr: date!) {
      pep_vw_consolidated_data(where: { dob: { _eq: $searchStr } } limit: 100) {
        pep_patient_id
        patient_clinic_data_id
        full_name
        dob
        home_phone_number
        mobile_phone_number
        email
        clinic_id
        clinic_name
        emr_id
      }
    }
  `,

  // Search Patients by Clinic
  SEARCH_PATIENTS_BY_CLINIC: `
    query SearchPatientsByClinic($clinicId: Int!, $searchStr: String!) {
      pep_vw_consolidated_data(
        where: {
          clinic_id: {_eq: $clinicId},
          search_string: {_iregex: $searchStr}
        }
      ) {
        patient_clinic_data_id
        full_name
        dob
        mobile_phone_number
        clinic_name
        provider_name
        emr_id
      }
    }
  `,

  // Patient Search List (from GRAPHQL_QUERIES)
  PATIENT_SEARCH_LIST: `
    query MyQuery($searchStr: String!) {
      pep_vw_consolidated_data(where: { search_string: { _ilike: $searchStr } } limit: 100) {
        pep_patient_id
        patient_clinic_data_id
        full_name
        dob
        home_phone_number
        mobile_phone_number
        email
        clinic_id
        clinic_name
        emr_id
      }
    }
  `,

  // Search Patients (from GRAPHQL_QUERIES)
  SEARCH_PATIENTS: `
    query SearchPatients($clinicId: Int!, $searchStr: String!) {
      pep_vw_consolidated_data(where: {ccm_status: {_nin: [1,2,3,7,8,9,10]}, search_string: {_iregex: $searchStr}, clinic_id: {_eq: $clinicId}}) {
        search_string
      }
    }
  `,

  // Get Patient by Search String (from GRAPHQL_QUERIES)
  GET_PATIENT_BY_SEARCH_STRING: `
    query GetPatientBySearchString($clinicId: Int!, $searchStr: String!) {
      pep_vw_consolidated_data(where: {ccm_status: {_nin: [1,2,3,7,8,9,10]}, clinic_id: {_eq: $clinicId}, search_string: {_eq: $searchStr}}) {
        patient_clinic_data_id
      }
    }
  `,

  // VPE Dashboard search query
  SEARCH_PATIENTS_VPE_DASHBOARD: `
    query SearchPatientsVPEDashboard($clinicID: Int!, $searchStr: String!) {
      pep_vw_consolidated_data(
        where: {
          ccm_status: {_nin: [1,2,3,7,8,9,10]},
          search_string: {_iregex: $searchStr},
          clinic_id: {_eq: $clinicID}
        }
      ) {
        patient_clinic_data_id
        full_name
        dob
        mobile_phone_number
        clinic_name
        provider_name
        emr_id
        ccm_status
        rpm_status
      }
    }
  `,

  // Two-step intelligent name search - exact match first, then word-by-word OR search
  SEARCH_PATIENTS_INTELLIGENT_NAME: `
    query SearchPatientsIntelligentName($exactMatch: String!, $wordSearch: String!) {
      exact_match: pep_vw_consolidated_data(
        where: { full_name: { _ilike: $exactMatch } }
        limit: 100
      ) {
        pep_patient_id
        patient_clinic_data_id
        full_name
        dob
        home_phone_number
        mobile_phone_number
        email
        clinic_id
        clinic_name
        emr_id
      }
      word_match: pep_vw_consolidated_data(
        where: { full_name: { _iregex: $wordSearch } }
        limit: 100
      ) {
        pep_patient_id
        patient_clinic_data_id
        full_name
        dob
        home_phone_number
        mobile_phone_number
        email
        clinic_id
        clinic_name
        emr_id
      }
    }
  `
};